import { useState } from "react";
import {
  Box,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  PasswordInput,
  Stack,
  Image,
  List,
  Alert,
} from "@mantine/core";
import { IconShieldCheck, IconAlertCircle } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { useUser } from "@/context/UserContext";

type Phase = "idle" | "scan";

/**
 * Enable or disable TOTP authenticator (Google Authenticator–compatible).
 * Protected route — user must be signed in.
 */
export default function EnableTwoFactorPage() {
  const location = useLocation();
  const { userDetails, refetchUser } = useUser();

  const token = Cookies.get(COOKIE_KEYS.token);
  let backHref = "/settings";
  let backLabel = "← Back to settings";
  try {
    if (token) {
      const d = jwtDecode<{ role?: string }>(token);
      if (d.role === "ADMIN" || location.pathname.startsWith("/admin/")) {
        backHref = "/admin/companies";
        backLabel = "← Back to admin";
      }
    }
  } catch {
    /* ignore */
  }
  const [phase, setPhase] = useState<Phase>("idle");
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const [disablePassword, setDisablePassword] = useState("");
  const [disableOtp, setDisableOtp] = useState("");

  const enabled = userDetails?.isTwoFactorEnabled === true;

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await trpc.twoFactor.setup.mutate({});
      setQrCode(res.qrCode);
      setManualCode(res.manualCode);
      setPhase("scan");
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setLoading(false);
    }
  };

  const verifyEnable = async () => {
    if (!/^\d{6}$/.test(otp.trim())) return;
    setLoading(true);
    try {
      const res = await trpc.twoFactor.verify.mutate({ otp: otp.trim() });
      setBackupCodes(res.backupCodes);
      refetchUser();
      successNotification({
        title: "2FA enabled",
        message: "Save your backup codes in a safe place.",
      });
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    if (!disablePassword.trim() || !disableOtp.trim()) return;
    setLoading(true);
    try {
      await trpc.twoFactor.disable.mutate({
        password: disablePassword,
        otp: disableOtp.trim(),
      });
      setDisablePassword("");
      setDisableOtp("");
      setPhase("idle");
      setQrCode(null);
      setManualCode(null);
      setBackupCodes(null);
      refetchUser();
      successNotification({ title: "2FA disabled", message: "Authenticator removed from your account." });
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p="xl" maw={560} mx="auto">
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="md">
          <Title order={3}>
            <IconShieldCheck size={24} style={{ verticalAlign: "middle", marginRight: 8 }} />
            Two-factor authentication
          </Title>
          <Text size="sm" c="dimmed">
            Add a second step to sign-in using an authenticator app (TOTP).
          </Text>

          {!enabled && phase === "idle" && (
            <Button onClick={startSetup} loading={loading}>
              Enable 2FA
            </Button>
          )}

          {!enabled && phase === "scan" && qrCode && manualCode && (
            <>
              <Text size="sm">Scan this QR code with Google Authenticator or another TOTP app.</Text>
              <Image src={qrCode} alt="Authenticator QR" maw={260} mx="auto" />
              <Text size="xs" c="dimmed">
                Manual entry: <code>{manualCode}</code>
              </Text>
              <TextInput
                label="6-digit code"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
              />
              <Button
                onClick={verifyEnable}
                loading={loading}
                disabled={!/^\d{6}$/.test(otp.trim())}
              >
                Verify and activate
              </Button>
            </>
          )}

          {backupCodes && backupCodes.length > 0 && (
            <Alert icon={<IconAlertCircle />} title="Backup codes" color="yellow">
              <Text size="sm" mb="xs">
                Store these one-time codes. Each can be used once if you lose your phone.
              </Text>
              <List size="sm">
                {backupCodes.map((c) => (
                  <List.Item key={c}>{c}</List.Item>
                ))}
              </List>
            </Alert>
          )}

          {enabled && (
            <>
              <Alert color="green" title="2FA is active">
                Sign-in will require a code from your authenticator app.
              </Alert>
              <Title order={5}>Disable 2FA</Title>
              <PasswordInput
                label="Password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.currentTarget.value)}
              />
              <TextInput
                label="Authenticator code or backup code"
                value={disableOtp}
                onChange={(e) => setDisableOtp(e.currentTarget.value.replace(/\D/g, "").slice(0, 8))}
                maxLength={8}
              />
              <Button color="red" variant="light" onClick={disable} loading={loading}>
                Disable 2FA
              </Button>
            </>
          )}

          <Link to={backHref} style={{ fontSize: 14 }}>
            {backLabel}
          </Link>
        </Stack>
      </Paper>
    </Box>
  );
}
